<script setup lang="ts">
import {
    Sale,
    BillAdjust,
    Pay,
    Sign,
    Profile,
    Printer,
    Note,
    DeleteOutlined,
    Swap,
    QrCode,
    IcArrowLeft,
    Contacts,
    IconPercent,
    ReceiptOutlined,
    IcCheckFalse,
    Inbox,
    ErrorOutlinedIcon,
    IconChange,
    IconCheck,
    IconStartShift,
    IconEndShift,
    VoidAndDuplicate,
    PayAtCounter,
    MergeOrders,
    KitchenSlip,
    StartOrder,
    CallNum,
    Order,
    CompleteOrderDocket,
    Timeline
} from '@xpos/icons-svg';
import { Close } from '@element-plus/icons-vue';
import { useI18n } from 'vue-i18n';
import { CalcType, IInvoice, CalcFlow, SectionItem, InvoiceStatus, OrderFlow, IMenu, InvoiceAction, IPaymentMethod } from '@posx/core';
import { DefineComponent, Ref, computed, inject, markRaw, onMounted, reactive, ref } from 'vue';
import PayInAndOutDialog from './PayInAndOutDialog.vue';
import NoteDialog from './NoteDialog.vue';
import DiscountDialog from './DiscountDialog.vue';
import CustomTotalDialog from './CustomTotalDialog.vue';
import SwitchTable from './SwitchTable.vue';
import Dialog from '@/components/Dialog.vue';
import Receipt from './Receipt.vue';
import DiscountSection from './DiscountSection.vue';
// 使用统一的StaffShift组件
import StaffShift from '@/components/StaffShift.vue';
import OrderQrCodeOptions from './OrderQrCodeOptions.vue';
import MenuQrCodeOptions from './MenuQrCodeOptions.vue';
import OrderTimelineDialog from './OrderTimelineDialog.vue';
import { TDiscountItem } from '@/types/discount.type';

import { IFunctionKeys, IFunctions } from './type';
import { useInvoiceStore } from '@/stores/invoice';
import { usePaymentStore } from '@/stores/payment';
import { ElMessageBox, ElNotification } from 'element-plus';
import { notify } from '@/utils/notification';
import { useConfigStore } from '@/stores/config';
import { DirectiveNames } from '../../../../acl/permissions';
import { useAcl } from '@/stores/acl';
import { usePrinterStore } from '@/stores/printer';
import { useEmployeeStore } from '@/stores/employee';
import { useSectionStore } from '@/stores/section';
import { useDevice } from '@/stores/device.store';
import { useShiftStore } from '@/stores/shift';
import { storeToRefs } from 'pinia';
import { getPlatform } from '@/utils/misc';

const currentInvoice: any = inject<Ref<IInvoice | null>>('invoice', ref(null));
const { t } = useI18n();
const invoiceStore = useInvoiceStore();
const sectionStore = useSectionStore();
const paymentStore = usePaymentStore();
const printerStore = usePrinterStore();
const employeeStore = useEmployeeStore();
const acl = useAcl();
const configStore = useConfigStore();
const shiftStore = useShiftStore();
const { currentShift } = storeToRefs(shiftStore);
const { appConfig } = storeToRefs(configStore);
const payType = ref('IN');
const visiblePayInAndOutDialog = ref(false);
const visibleSwitchTable = ref(false);
const visibleNoteDialog = ref(false);
const visibleDiscount = ref(false);
const discountType = ref<CalcType>();
const visibleCustomTotal = ref(false);
const visibleStaffShift = ref(false);
const visibleOrderQrOptions = ref(false);
const visibleMenuQrOptions = ref(false);
const visibleOrderTimeline = ref(false);
const qrOrderFlow = ref<OrderFlow>();
const visible = reactive({
    receipt: false,
});

interface Props {
    functions: IFunctionKeys[] | IFunctions[];
    hasTitle?: boolean;
    hasDiscount?: boolean;
}

const $props = withDefaults(defineProps<Props>(), {
    hasTitle: true,
    hasDiscount: true
});
const $emit = defineEmits(['action']);

const functions = computed(() => {
    let funcs: IFunctions[] = [];
    if (Array.isArray($props.functions)) {
        funcs = $props.functions.map((item) => {
            if (typeof item === 'string') {
                return {
                    key: item,
                    ...FUNCTION_MAP[item],
                } as IFunctions;
            }
            return { ...FUNCTION_MAP[item.key!], ...item } as IFunctions;
        });
    }
    return funcs;
});

const isVoid = computed(() => currentInvoice?.value?.status === InvoiceStatus.Void);
// Mobile/desktop-web has no employee role, skip permission-based visual disable
const skipPermVisual = getPlatform() === 'mobile-web' || getPlatform() === 'desktop-web';
// Pure check for visual disable
const noPermission = (func: IFunctions) => !skipPermVisual && !!func.permission && !acl.checkPermission(func.permission);
// Guard click by permission here (not just inside each FUNCTION_MAP click), since callers can override click with their own handler that skips the check
const handleFuncClick = (func: IFunctions) => { if (!func.permission || acl.requirePermission(func.permission)) func.click?.(); };

const FUNCTION_MAP: { [key in IFunctionKeys]: Omit<IFunctions, 'key'> } = {
    switchTable: {
        title: t('function.buttons.switch_table'),
        icon: Swap,
        permission: DirectiveNames.FUNCTION_SWITCH_TABLE,
        disabled: (!currentInvoice?.value?.id_in_server && !currentInvoice?.value?.lines?.length) || currentInvoice?.value?.action === InvoiceAction.PayAtCounter,
        click: () => {
            if (acl.hasPermission(DirectiveNames.FUNCTION_SWITCH_TABLE)) visibleSwitchTable.value = true;
        },
    },
    lastBill: {
        title: t('function.buttons.last_bill'),
        icon: Printer,
        permission: DirectiveNames.FUNCTION_LAST_BILL,
        disabled: currentInvoice?.value?.action === InvoiceAction.PayAtCounter,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_LAST_BILL)) $emit('action', 'lastBill');
        },
    },
    printLastKitchenSlip: {
        title: t('function.buttons.print_last_kitchen_slip'),
        icon: KitchenSlip,
        permission: DirectiveNames.FUNCTION_PRINT_LAST_KITCHEN_SLIP,
        disabled: (!currentInvoice?.value?.id_in_server && !currentInvoice?.value?.lines?.length) || !appConfig?.value?.config?.kitchen?.enabled,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_PRINT_LAST_KITCHEN_SLIP)) $emit('action', 'printLastKitchenSlip');
        },
    },
    printLastOrderDocket: {
        title: t('function.buttons.print_last_order_docket'),
        icon: Order,
        permission: DirectiveNames.FUNCTION_PRINT_LAST_ORDER_DOCKET,
        disabled: (!currentInvoice?.value?.id_in_server && !currentInvoice?.value?.lines?.length) || !appConfig?.value?.config?.receipt?.print_docket,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_PRINT_LAST_ORDER_DOCKET)) $emit('action', 'printLastOrderDocket');
        },
    },
    printFullOrderDocket: {
        title: t('function.buttons.print_full_order_docket'),
        icon: CompleteOrderDocket,
        permission: DirectiveNames.FUNCTION_PRINT_FULL_ORDER_DOCKET,
        disabled: !appConfig?.value?.config?.receipt?.print_docket,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_PRINT_FULL_ORDER_DOCKET)) $emit('action', 'printFullOrderDocket');
        },
    },
    note: {
        title: t('function.buttons.note'),
        icon: Note,
        permission: DirectiveNames.FUNCTION_NOTE,
        disabled: !currentInvoice?.value?.id_in_server && !currentInvoice?.value?.lines?.length,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_NOTE)) visibleNoteDialog.value = true;
        },
    },
    deleteBill: {
        title: t('function.buttons.delete_bill'),
        icon: DeleteOutlined,
        permission: DirectiveNames.FUNCTION_DELETE_BILL,
        disabled: !currentInvoice?.value?.id_in_server && !currentInvoice?.value?.lines?.length,
        click: () => {
            if (acl.hasPermission(DirectiveNames.FUNCTION_DELETE_BILL)) $emit('action', 'deleteBill');
        },
    },
    percentDiscount: {
        title: t('function.buttons.percent_discount'),
        icon: IconPercent,
        permission: DirectiveNames.FUNCTION_PERCENT_DISCOUNT,
        disabled: !currentInvoice?.value?.id_in_server && !currentInvoice?.value?.lines?.length,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_PERCENT_DISCOUNT)) {
                discountType.value = CalcType.Percent;
                visibleDiscount.value = true;
            }
        },
    },
    flatDiscount: {
        title: t('function.buttons.flat_discount'),
        icon: Sale,
        permission: DirectiveNames.FUNCTION_FLAT_DISCOUNT,
        disabled: !currentInvoice?.value?.id_in_server && !currentInvoice?.value?.lines?.length,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_FLAT_DISCOUNT)) {
                discountType.value = CalcType.Flat;
                visibleDiscount.value = true;
            }
        },
    },
    customTotal: {
        title: t('function.buttons.custom_total'),
        icon: BillAdjust,
        permission: DirectiveNames.FUNCTION_FLAT_DISCOUNT,
        disabled: !currentInvoice?.value?.id_in_server && !currentInvoice?.value?.lines?.length,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_FLAT_DISCOUNT)) visibleCustomTotal.value = true;
        },
    },
    qrCode: {
        title: t('function.buttons.order_qr'),
        icon: QrCode,
        permission: DirectiveNames.FUNCTION_QR_CODE,
        disabled: currentInvoice?.value?.action === InvoiceAction.PayAtCounter,
        click: async () => {
            if (!acl.hasPermission(DirectiveNames.FUNCTION_QR_CODE)) return;
            if (!sectionStore.currentSectionItem) return notify.error(t('common.error'));

            if (appConfig.value?.config?.online_order?.dynamic_order_qr?.select_order_flow) {
                visibleOrderQrOptions.value = true;
            } else {
                qrOrderFlow.value = appConfig.value?.config?.online_order?.dynamic_order_qr?.default_order_flow ?? OrderFlow.OrderFirstAndPayLater;
                checkMenuSelection();
            }
        },
    },
    payIn: {
        title: t('function.buttons.pay_in'),
        icon: IcArrowLeft,
        class: '-rotate-90',
        permission: DirectiveNames.FUNCTION_PAY_IN,
        click: () => {
            if (acl.hasPermission(DirectiveNames.FUNCTION_PAY_IN)) {
                payType.value = 'IN';
                visiblePayInAndOutDialog.value = true;
            }
        },
    },
    payOut: {
        title: t('function.buttons.pay_out'),
        icon: IcArrowLeft,
        class: 'rotate-90',
        permission: DirectiveNames.FUNCTION_PAY_OUT,
        click: () => {
            if (acl.hasPermission(DirectiveNames.FUNCTION_PAY_OUT)) {
                payType.value = 'OUT';
                visiblePayInAndOutDialog.value = true;
            }
        },
    },
    openDrawer: {
        debounceTime: 1000,
        title: t('function.buttons.open_drawer'),
        icon: Inbox,
        permission: DirectiveNames.FUNCTION_OPEN_DRAWER,
        click: async () => {
            if (acl.hasPermission(DirectiveNames.FUNCTION_OPEN_DRAWER)) {
                const deviceStore = useDevice();
                await deviceStore.openCashDrawer(true);
            }
        },
    },
    void: {
        title: t('function.buttons.void'),
        icon: IcCheckFalse,
        permission: DirectiveNames.FUNCTION_VOID,
        get disabled() { return isVoid.value },
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_VOID)) {
                ElMessageBox.confirm(t('function.deleteConfirm.title'), 'Error', {
                    confirmButtonText: t('function.deleteConfirm.ok'),
                    cancelButtonText: t('function.deleteConfirm.cancel'),
                    type: 'error',
                    icon: markRaw(ErrorOutlinedIcon),
                    autofocus: false,
                }).then(async () => {
                    try {
                        await handleVoidPayment();
                        ElNotification({
                            customClass: 'x-notification__success',
                            title: t('common.success'),
                            message: t('common.operationSuccessful'),
                            type: 'success',
                        });
                    } catch (e) {
                        notify.error((e as Error).message);
                    }
                }).catch(() => {}); // Ignore cancel action
            }
        },
    },
    receipt: {
        title: t('function.buttons.receipt'),
        icon: ReceiptOutlined,
        permission: DirectiveNames.FUNCTION_RECEIPT,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_RECEIPT)) {
                visible.receipt = true;
            }
        },
    },
    reprint: {
        title: t('function.buttons.reprint'),
        icon: Printer,
        permission: DirectiveNames.FUNCTION_REPRINT,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_REPRINT)) {
                // Add the desired behavior for reprint function
            }
        },
    },
    addCustomer: {
        title: t('function.buttons.add_customer'),
        icon: Contacts,
        permission: DirectiveNames.FUNCTION_ADD_CUSTOMER,
        disabled: currentInvoice?.value?.customer_id !== 0,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_ADD_CUSTOMER)) {
                // Add the desired behavior for addCustomer function
            }
        },
    },
    changePayment: {
        title: t('function.buttons.change_payment'),
        icon: IconChange,
        permission: DirectiveNames.FUNCTION_CHANGE_PAYMENT,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_CHANGE_PAYMENT)) {
                // Add the desired behavior for changePayment function
            }
        },
    },
    check: {
        title: t('function.buttons.check'),
        icon: IconCheck,
        permission: DirectiveNames.FUNCTION_CHECK,
        disabled: !currentInvoice?.value?.id_in_server && !currentInvoice?.value?.lines?.length,
        click: async () => {
            $emit('action', 'check');
        },
    },
    startShift: {
        title: t('function.buttons.start_shift'),
        icon: IconStartShift,
        get disabled() {
            return !!currentShift.value; // 有班次时禁用开班按钮
        },
        click: () => {
            visibleStaffShift.value = true;
        },
    },
    endShift: {
        title: t('function.buttons.end_shift'),
        icon: IconEndShift,
        get disabled() {
            return !currentShift.value; // 无班次时禁用结班按钮
        },
        click: async () => {
            await shiftStore.calculateAndUpdateDrawerAmount();
            visibleStaffShift.value = true;
        },
    },
    voidAndDuplicate: {
        title: t('function.buttons.void_and_duplicate'),
        icon: VoidAndDuplicate,
        permission: DirectiveNames.FUNCTION_VOID_AND_DUPLICATE,
        click() {
        $emit('action', 'voidAndDuplicate');
        },
    },
    payAtCounter: {
        title: t('function.buttons.pay_at_counter'),
        icon: PayAtCounter,
        permission: DirectiveNames.FUNCTION_PAY_AT_COUNTER,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_PAY_AT_COUNTER)) {
                $emit('action', 'payAtCounter');
            }
        },
    },
    mergeTable: {
        title: t('function.buttons.merge_table'),
        icon: MergeOrders,
        permission: DirectiveNames.FUNCTION_MERGE_TABLE,
        disabled: !currentInvoice?.value?.id_in_server && !currentInvoice?.value?.lines?.length,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_MERGE_TABLE)) {
                $emit('action', 'mergeTable');
            }
        },
    },
    addSalesperson: {
        title: t('function.buttons.add_salesperson'),
        icon: Profile,
        permission: DirectiveNames.FUNCTION_ADD_SALESPERSON,
        click() {
            if (acl.hasPermission(DirectiveNames.FUNCTION_ADD_SALESPERSON)) $emit('action', 'addSalesperson');
        },
    },
    startOrder: {
        title: t('function.buttons.start_order'),
        icon: StartOrder,
        debounceTime: 1000,
        click() {
            $emit('action', 'startOrder');
        },
    },
    callNum: {
        title: t('function.buttons.custom_order_num'),
        icon: CallNum,
        click() {
            $emit('action', 'callNum');
        },
    },
    orderTimeline: {
        title: t('function.buttons.order_timeline'),
        icon: Timeline,
        disabled: !currentInvoice?.value?.id_in_server,
        click() { visibleOrderTimeline.value = true; },
    },
};

const handlePay = async (amount: number, note: string, paymentMethod: IPaymentMethod | null) => {
    $emit('action', payType.value == 'IN' ? 'payIn' : 'payOut', amount, note, paymentMethod);
    visiblePayInAndOutDialog.value = false;
};

const handleSwitchTable = async (value: SectionItem) => {
    await $emit('action', 'switchTable', value);
    visibleSwitchTable.value = false;
};

const handleNoteChange = async (val: string) => {
    if (!currentInvoice?.value) {
        return notify.error('The Current invoice does not exist');
    }
    const invoiceService = await invoiceStore.getService();
    if (!val) currentInvoice.value.remark = '';
    await invoiceService.overwriteNoteToInvoice(currentInvoice?.value!, val);

    // Sync invoice after note change
    if (currentInvoice?.value) {
        await invoiceStore.syncInvoice(currentInvoice.value);
    }

    visibleNoteDialog.value = false;
};

const handleDiscountChange = async (discount: TDiscountItem) => {
    const invoiceService = await invoiceStore.getService();
    await invoiceService.addInvoiceDiscount(currentInvoice?.value!,
    discount.calc_type,
    discount.calc_type === CalcType.Percent ? discount.percent : discount.amount,
    CalcFlow.AfterSubtotal,
    discount.name,
    discount.name_translations);
    await invoiceStore.calculate(currentInvoice?.value!);
    if (currentInvoice?.value) {
        await invoiceStore.syncInvoice(currentInvoice.value);
    }
    visibleDiscount.value = false;
};

const handleVoidPayment = async () => {
    await paymentStore.removePayment(currentInvoice?.value!, currentInvoice?.value!.payments[0]?.uid!);
};

// Check if menu selection is needed, otherwise generate QR directly
const checkMenuSelection = () => {
    if (appConfig.value?.config?.online_order?.dynamic_order_qr?.select_menus) {
        visibleMenuQrOptions.value = true;
    } else {
        generateQrCode();
    }
};

// Generate QR code with current settings
const generateQrCode = async (menu?: IMenu) => {
    if (!sectionStore.currentSectionItem || !qrOrderFlow.value) return notify.error(t('common.error'));
    try {
        await printerStore.createQrcodePrintJob(sectionStore.currentSectionItem, qrOrderFlow.value, menu);
        notify.success(t('common.success'));
    } catch (e: any) {
        const msg = e?.message === 'scan_order_service_not_configured' ? t('common.scan_order_service_not_configured') : t('common.error');
        ElNotification({ customClass: 'x-notification__error', title: t('common.error'), message: msg });
    }
};

const handleOrderQrSelection = (option: OrderFlow) => {
    qrOrderFlow.value = option;
    visibleOrderQrOptions.value = false;
    checkMenuSelection();
};

const handleMenuQrSelection = (menu: IMenu) => {
    visibleMenuQrOptions.value = false;
    generateQrCode(menu);
};

onMounted( async () => {
    await configStore.startLiveQuery();
    await shiftStore.startShiftLiveQuery();
});
</script>
<template>
    <div class="w-full">
        <div v-if="hasTitle">{{ t('function.title') }}</div>
        <div class="btn-group">
            <x-button type="primary" square size="large" v-for="func in functions" @click="handleFuncClick(func)"
                :disabled="func.disabled" :debounceTime="func.debounceTime"
                :class="{ 'no-permission': noPermission(func) }">
                <el-icon size="50" :class="func.class">
                    <component :is="func.icon" />
                </el-icon>
                <template #footer>
                    <span :style="{ fontSize: func.titleSize || '13px' }">{{ func.title }}</span>
                </template>
            </x-button>
        </div>
        <DiscountSection v-if="hasDiscount" />
        <StaffShift v-model="visibleStaffShift" />
        <PayInAndOutDialog v-model="visiblePayInAndOutDialog" :type="payType" @confirm="handlePay" />
        <SwitchTable v-model="visibleSwitchTable" @confirm="handleSwitchTable" />
        <NoteDialog v-model="visibleNoteDialog" model="invoice_remark" :data="currentInvoice?.remark" @confirm="handleNoteChange" />
        <DiscountDialog v-model="visibleDiscount" :discountType="discountType"
            :data="currentInvoice?.discount_amount! || 0"
            :totalAmount="currentInvoice?.discountable_subtotal || 0"
            @confirm="handleDiscountChange" />
        <CustomTotalDialog v-model="visibleCustomTotal" :totalAmount="currentInvoice?.grand_total || 0"
            @confirm="handleDiscountChange" />
        <OrderQrCodeOptions v-model="visibleOrderQrOptions" @confirm="handleOrderQrSelection" />
        <MenuQrCodeOptions v-model="visibleMenuQrOptions" @confirm="handleMenuQrSelection" />
        <OrderTimelineDialog v-model="visibleOrderTimeline" />
        <Dialog title="View Receipt" v-model="visible.receipt">
            <div class="w-[360px]">
                ====================================================
                <Receipt />
            </div>
        </Dialog>
    </div>
</template>
<style lang="scss">
.btn-group {
    .el-button.is-disabled,
    .el-button.is-disabled:focus,
    .el-button.is-disabled:hover {
        background: #52599D80;
    }
    /* Visual disabled for missing permission; button stays clickable to fire the notification */
    .no-permission .el-button,
    .no-permission .el-button:focus,
    .no-permission .el-button:hover {
        background: #52599D80;
        opacity: 0.7;
    }
}
</style>
